import { useState, useEffect, useCallback } from 'react';
import { passwordProblem } from './Register';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { User, Users, Bell, Plus, Database, Key, Copy, CheckCircle, LockKey, EnvelopeSimple, ClockCounterClockwise, AirplaneTilt } from '@phosphor-icons/react';
import EmailConfigPanel from '../components/settings/EmailConfigPanel';
import DataManagementPanel from '../components/settings/DataManagementPanel';
import { CreateUserDialog, EditUserDialog } from '../components/settings/UserFormDialogs';
import { PendingUsersTab, AllUsersTab } from '../components/settings/UserManagementTabs';
import LoginAuditTab from '../components/settings/LoginAuditTab';
import EccairsSettingsPanel from '../components/settings/EccairsSettingsPanel';

const API = '/api';

export default function Settings() {
  const { user } = useAuth();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const [createForm, setCreateForm] = useState({
    name: '', email: '', password: '', sector: 'aviation', role: 'investigator',
    specializations: [], phone: '', designation: '', organization: '',
    years_of_experience: 0, investigations_completed: 0, certifications: [],
    primary_specialty: '', qualifications: ''
  });
  const [certInput, setCertInput] = useState('');

  const [editForm, setEditForm] = useState({
    name: '', sector: '', role: '', specializations: [], status: '',
    phone: '', designation: '', organization: '', years_of_experience: 0,
    investigations_completed: 0, certifications: [], primary_specialty: '', qualifications: ''
  });
  const [editCertInput, setEditCertInput] = useState('');

  // Admin reset password
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const [resetPasswordEmail, setResetPasswordEmail] = useState('');
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetCopied, setResetCopied] = useState(false);

  // Change own password
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      const [pendingRes, allRes] = await Promise.all([
        axios.get(`${API}/admin/users/pending`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true })
      ]);
      setPendingUsers(pendingRes.data);
      setAllUsers(allRes.data);
    } catch (error) { console.error('Failed to load users:', error); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') loadUsers();
    else setLoading(false);
  }, [user?.role, loadUsers]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/users/create`, createForm, { withCredentials: true });
      alert('User created successfully!');
      setIsCreateOpen(false);
      setCreateForm({ name: '', email: '', password: '', sector: 'aviation', role: 'investigator', specializations: [], phone: '', designation: '', organization: '', years_of_experience: 0, investigations_completed: 0, certifications: [], primary_specialty: '', qualifications: '' });
      setCertInput(''); loadUsers();
    } catch (error) { alert(error.response?.data?.detail || 'Error creating user'); }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/users/${editUser.email}/update`, editForm, { withCredentials: true });
      alert('User updated successfully!');
      setIsEditOpen(false); setEditUser(null); loadUsers();
    } catch (error) { alert(error.response?.data?.detail || 'Error updating user'); }
  };

  const handleToggleActive = async (email, currentStatus) => {
    if (window.confirm(`Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this user?`)) {
      try { await axios.put(`${API}/admin/users/${email}/toggle-active`, {}, { withCredentials: true }); loadUsers(); }
      catch (error) { alert('Error toggling user status'); }
    }
  };

  const handleAdminResetPassword = async () => {
    setResetPasswordLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/admin-reset-password`, { email: resetPasswordEmail }, { withCredentials: true });
      setResetPasswordResult(data);
    } catch (error) { alert(error.response?.data?.detail || 'Error resetting password'); }
    finally { setResetPasswordLoading(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault(); setChangePasswordError('');
    if (newPassword !== confirmPassword) { setChangePasswordError('New passwords do not match'); return; }
    { const problem = passwordProblem(newPassword); if (problem) { setChangePasswordError(problem); return; } }
    setChangePasswordLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword }, { withCredentials: true });
      alert('Password changed successfully!');
      setChangePasswordOpen(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (error) { setChangePasswordError(error.response?.data?.detail || 'Error changing password'); }
    finally { setChangePasswordLoading(false); }
  };

  const openEditDialog = (u) => {
    setEditUser(u);
    setEditForm({
      name: u.name, sector: u.sector || 'aviation', role: u.role, specializations: u.specializations || [],
      status: u.status, phone: u.phone || '', designation: u.designation || '', organization: u.organization || '',
      years_of_experience: u.years_of_experience || 0, investigations_completed: u.investigations_completed || 0,
      certifications: u.certifications || [], primary_specialty: u.primary_specialty || '', qualifications: u.qualifications || ''
    });
    setEditCertInput(''); setIsEditOpen(true);
  };

  const handleApprove = async (email) => {
    try { await axios.post(`${API}/admin/users/${email}/approve`, {}, { withCredentials: true }); alert('User approved!'); loadUsers(); }
    catch (error) { alert('Error approving user'); }
  };

  const handleReject = async (email) => {
    if (window.confirm('Are you sure you want to reject this user?')) {
      try { await axios.post(`${API}/admin/users/${email}/reject`, {}, { withCredentials: true }); alert('User rejected'); loadUsers(); }
      catch (error) { alert('Error rejecting user'); }
    }
  };

  return (
    <div className="" data-testid="settings-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>Settings</h1>
          <p className="text-slate-500">Manage your account and system settings</p>
        </div>
        {user?.role === 'admin' && (
          <Button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg" onClick={() => setIsCreateOpen(true)}>
            <Plus size={18} className="mr-2" /> Create User
          </Button>
        )}
      </div>

      {loading ? (
        <div className="bg-white rounded-xl p-12 border border-slate-200">Loading...</div>
      ) : (
        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1">
            <TabsTrigger value="profile" className="rounded-lg"><User size={18} className="mr-2" />Profile</TabsTrigger>
            {user?.role === 'admin' && (
              <>
                <TabsTrigger value="pending" className="rounded-lg"><Bell size={18} className="mr-2" />Pending ({pendingUsers.length})</TabsTrigger>
                <TabsTrigger value="users" className="rounded-lg"><Users size={18} className="mr-2" />All Users ({allUsers.length})</TabsTrigger>
                <TabsTrigger value="data" className="rounded-lg"><Database size={18} className="mr-2" />Data Management</TabsTrigger>
                <TabsTrigger value="email" className="rounded-lg"><EnvelopeSimple size={18} className="mr-2" />Email Config</TabsTrigger>
                <TabsTrigger value="audit" className="rounded-lg"><ClockCounterClockwise size={18} className="mr-2" />Login Audit</TabsTrigger>
                <TabsTrigger value="eccairs" className="rounded-lg"><AirplaneTilt size={18} className="mr-2" />ECCAIRS</TabsTrigger>
              </>
            )}
          </TabsList>

          <TabsContent value="profile">
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>Profile Information</h2>
              <div className="space-y-4">
                <div><label className="text-sm font-medium text-slate-700">Name</label><div className="mt-1 text-base text-slate-900">{user?.name}</div></div>
                <div><label className="text-sm font-medium text-slate-700">Email</label><div className="mt-1 text-base text-slate-900">{user?.email}</div></div>
                <div><label className="text-sm font-medium text-slate-700">Role</label><div className="mt-1 text-base text-slate-900 capitalize">{user?.role}</div></div>
              </div>
            </div>
          </TabsContent>

          {user?.role === 'admin' && (
            <>
              <TabsContent value="pending"><PendingUsersTab pendingUsers={pendingUsers} onApprove={handleApprove} onReject={handleReject} /></TabsContent>
              <TabsContent value="users">
                <AllUsersTab users={allUsers} onEdit={openEditDialog}
                  onResetPassword={(email) => { setResetPasswordEmail(email); setResetPasswordResult(null); setResetCopied(false); setResetPasswordDialog(true); }}
                  onToggleActive={handleToggleActive} />
              </TabsContent>
              <TabsContent value="data"><DataManagementPanel /></TabsContent>
              <TabsContent value="email"><EmailConfigPanel /></TabsContent>
              <TabsContent value="audit"><LoginAuditTab /></TabsContent>
              <TabsContent value="eccairs"><EccairsSettingsPanel /></TabsContent>
            </>
          )}
        </Tabs>
      )}

      {/* Change Password */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mt-6" data-testid="change-password-section">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center shrink-0"><LockKey size={24} className="text-amber-600" /></div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Change Password</h3>
            <p className="text-sm text-slate-500 mt-1">Update your account password. You'll need to enter your current password.</p>
            {!changePasswordOpen ? (
              <Button className="mt-4 bg-amber-600 hover:bg-amber-700 text-white rounded-lg" onClick={() => setChangePasswordOpen(true)} data-testid="change-password-btn">
                <Key size={18} className="mr-2" /> Change Password
              </Button>
            ) : (
              <form onSubmit={handleChangePassword} className="mt-4 space-y-3 max-w-sm">
                <div><Label className="text-xs">Current Password</Label><Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter current password" className="rounded-lg" data-testid="current-password" required /></div>
                <div><Label className="text-xs">New Password</Label><Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password (min 6 chars)" className="rounded-lg" data-testid="new-password" required /></div>
                <div><Label className="text-xs">Confirm New Password</Label><Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" className="rounded-lg" data-testid="confirm-password" required /></div>
                {changePasswordError && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm" data-testid="change-password-error">{changePasswordError}</div>}
                <div className="flex gap-2">
                  <Button type="submit" disabled={changePasswordLoading} className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg" data-testid="change-password-submit">{changePasswordLoading ? 'Changing...' : 'Update Password'}</Button>
                  <Button type="button" variant="outline" onClick={() => { setChangePasswordOpen(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); setChangePasswordError(''); }} className="rounded-lg">Cancel</Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Admin Reset Password Dialog */}
      <Dialog open={resetPasswordDialog} onOpenChange={(open) => { if (!open) { setResetPasswordDialog(false); setResetPasswordResult(null); setResetCopied(false); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Key size={22} weight="duotone" className="text-amber-600" /> Reset User Password
            </DialogTitle>
          </DialogHeader>
          {!resetPasswordResult ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">A new temporary password will be generated for this user. Share it securely with them.</p>
              <div className="bg-slate-50 border border-slate-200 p-3 rounded-lg">
                <span className="text-xs text-slate-500 uppercase block mb-1">User</span>
                <span className="text-sm font-medium text-slate-900">{resetPasswordEmail}</span>
              </div>
              <div className="flex gap-3">
                <Button onClick={handleAdminResetPassword} disabled={resetPasswordLoading} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg" data-testid="admin-reset-confirm">
                  {resetPasswordLoading ? 'Resetting...' : 'Generate Temporary Password'}
                </Button>
                <Button variant="outline" onClick={() => setResetPasswordDialog(false)} className="rounded-lg">Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="admin-reset-success">
              <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 p-3 rounded-lg">
                <CheckCircle size={20} weight="fill" /><span className="text-sm font-medium">{resetPasswordResult.message}</span>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase mb-1.5">Temporary Password</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-slate-100 border border-slate-300 px-4 py-3 rounded-lg text-sm font-mono font-bold text-slate-900 select-all" data-testid="admin-temp-password">{resetPasswordResult.temporary_password}</code>
                  <Button variant="outline" onClick={() => { navigator.clipboard.writeText(resetPasswordResult.temporary_password); setResetCopied(true); setTimeout(() => setResetCopied(false), 2000); }} className="rounded-lg h-[46px] px-3" data-testid="admin-copy-temp-password">
                    {resetCopied ? <CheckCircle size={18} className="text-green-600" /> : <Copy size={18} />}
                  </Button>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg"><p className="text-xs text-amber-800">Share this password securely with the user. They should change it immediately after logging in.</p></div>
              <Button onClick={() => { setResetPasswordDialog(false); setResetPasswordResult(null); }} className="w-full bg-slate-600 hover:bg-slate-700 text-white rounded-lg">Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create / Edit User Dialogs */}
      <CreateUserDialog open={isCreateOpen} onOpenChange={setIsCreateOpen} form={createForm} setForm={setCreateForm} certInput={certInput} setCertInput={setCertInput} onSubmit={handleCreateUser} />
      {user?.role === 'admin' && <EditUserDialog open={isEditOpen} onOpenChange={setIsEditOpen} editUser={editUser} form={editForm} setForm={setEditForm} certInput={editCertInput} setCertInput={setEditCertInput} onSubmit={handleEditUser} />}
    </div>
  );
}
